import { useUnit } from 'effector-react';
import { Building2, FileText, KeyRound, Landmark, Sunset } from 'lucide-react';
import { RadioGroup } from 'radix-ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogClose, DialogContent, Field, Input } from '../../shared/ui';
import { projectCreated } from './model';
import { buildLocalizedProject } from './localized';
import { PROJECT_TEMPLATES, type ProjectTemplate } from './templates';

const ICONS: Record<ProjectTemplate, ReactNode> = {
  blank: <FileText />,
  rentVsBuy: <Building2 />,
  earlyPayoff: <Landmark />,
  retireEarly: <Sunset />,
  buyToLet: <KeyRound />,
};

export interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTemplate?: ProjectTemplate;
}

export function CreateProjectDialog({
  open,
  onOpenChange,
  initialTemplate = 'rentVsBuy',
}: CreateProjectDialogProps) {
  const { t } = useTranslation();
  const create = useUnit(projectCreated);
  const [template, setTemplate] = useState<ProjectTemplate>(initialTemplate);
  const [name, setName] = useState('');

  const submit = (): void => {
    const project = buildLocalizedProject(
      template,
      name.trim() ||
        (template === 'blank'
          ? t('createProject.defaultName')
          : t(`createProject.templates.${template}.title`)),
      t,
    );
    create(project);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t('createProject.title')}
        description={t('createProject.description')}
        closeLabel={t('common.close')}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">{t('common.cancel')}</Button>
            </DialogClose>
            <Button variant="primary" onClick={submit}>
              {t('createProject.create')}
            </Button>
          </>
        }
      >
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <RadioGroup.Root
            aria-label={t('createProject.title')}
            value={template}
            onValueChange={(v) => {
              setTemplate(v as ProjectTemplate);
            }}
            className="grid gap-2"
          >
            {PROJECT_TEMPLATES.map((key) => (
              <RadioGroup.Item
                key={key}
                value={key}
                className="flex items-start gap-3 rounded-lg border border-border-subtle bg-bg-1 p-3 text-left outline-none hover:bg-bg-3 focus-visible:ring-2 focus-visible:ring-focus data-[state=checked]:border-accent data-[state=checked]:bg-accent-subtle"
              >
                <span className="mt-0.5 text-text-3 [&_svg]:size-4">{ICONS[key]}</span>
                <span>
                  <span className="block text-sm font-medium">
                    {t(`createProject.templates.${key}.title`)}
                  </span>
                  <span className="block text-xs text-text-3">
                    {t(`createProject.templates.${key}.body`)}
                  </span>
                </span>
              </RadioGroup.Item>
            ))}
          </RadioGroup.Root>
          <Field label={t('createProject.name')}>
            {({ id }) => (
              <Input
                id={id}
                value={name}
                placeholder={
                  template === 'blank'
                    ? t('createProject.defaultName')
                    : t(`createProject.templates.${template}.title`)
                }
                onChange={(e) => {
                  setName(e.currentTarget.value);
                }}
              />
            )}
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  );
}
